/* eslint-disable react/jsx-handler-names */
import React, { useContext, useMemo, useRef, useState } from 'react';
import * as PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import cn from 'classnames';

import Icon from '../../../Icon';
import styles from './styles.styl';
import CallDuration from '../CallDuration';
import { COLORS } from '../../../../constants/styles';
import { IconButton } from '../../../Button/IconButton';
import ClickableElement from '../../../ClickableElement';
import { OTContext } from '../../../../redux/opentok/context';
import { useToggleFlag } from '../../../../hocs/useToogleFlag';
import brandConfig, { isFlagler } from '../../../../constants/brandConfig';
import { useVirtualVisit } from '../../../../redux/opentok/hooks/useVirtualVisit';
import MediaSettings from '../MediaSettings';

function VisitHeader({
  full,
  noInfo,
  collapsed,
  noDescription,
  intl: { formatMessage },
  roomDetails: { name, number, connectionRoom, visitType } = {},
}) {
  const { viewOptions } = useVirtualVisit();
  const { state } = useContext(OTContext);
  const [opened, open, close] = useToggleFlag(false);
  const [visible, showDescription] = useState(!noDescription);
  const { durationData, connectionTime, connections } = { ...state };

  const {
    duration,
    timerActive,
    currentTimestamp,
    currentSessionStartedAt,
  } = durationData;

  const providerJoinedAt = useRef(null);

  const hasProviderConnected =
    !connectionRoom &&
    Object.values(connections || {}).some(
      conn => conn.user && conn.user.isProvider,
    );

  if (hasProviderConnected && !providerJoinedAt.current) {
    providerJoinedAt.current = new Date().toISOString();
  } else if (!hasProviderConnected) {
    providerJoinedAt.current = null;
  }

  const effectiveTimerActive = timerActive || hasProviderConnected;
  const effectiveStartedAt =
    currentSessionStartedAt || providerJoinedAt.current;

  const title = useMemo(() => {
    if (name) {
      const resolvedVisitType =
        visitType || formatMessage({ id: 'virtualVisit.virtualVisit' });
      return formatMessage(
        { id: 'virtualVisit.title' },
        { name, visitType: resolvedVisitType },
      );
    }
    if (connectionRoom) {
      return formatMessage({ id: 'common.call' });
    }
    return formatMessage({ id: 'virtualVisit.virtualVisit' });
  }, [name, connectionRoom, visitType]);

  return (
    <div
      className={cn(styles.root, {
        [styles.full]: full,
        [styles.collapsed]: collapsed || viewOptions.fullScreenView,
      })}
    >
      <div className={styles.main}>
        <div className={styles.logo}>
          <Icon
            name={brandConfig.logo}
            size={brandConfig.logoSize}
            style={{ ...(isFlagler && { fill: brandConfig.fillColor }) }}
          />
        </div>
        <div className={styles.title}>{title}</div>
      </div>

      {!noInfo && (
        <div className={styles.info}>
          <div className={styles.text}>
            {formatMessage({ id: 'virtualVisit.room' })}:&nbsp;{number}
          </div>
          <div className={styles.text}>
            {formatMessage({ id: 'virtualVisit.duration' })}:&nbsp;
            <CallDuration
              time={connectionTime}
              timerActive={effectiveTimerActive}
              previousDuration={duration}
              isConnectionRoom={connectionRoom}
              currentTimestamp={currentTimestamp}
              currentSessionStartedAt={effectiveStartedAt}
            />
          </div>
          <div>
            <ClickableElement
              onClick={viewOptions.toggleMobSidebar}
              className={styles.infoBtn}
            >
              <Icon name="info-default" size={24} />
            </ClickableElement>
            <div className={styles.deviceSettings}>
              <ClickableElement onClick={open} className={styles.btn}>
                <Icon name="settings" size={24} />
              </ClickableElement>
            </div>
            <MediaSettings opened={opened} close={close} />
          </div>
        </div>
      )}

      {visible && (
        <div className={styles.desc}>
          <div>
            <span className={styles.descText}>
              {formatMessage({ id: 'virtualVisit.test.description' })}
            </span>
            <ClickableElement
              className={styles.descLink}
              onClick={() => open()}
            >
              {formatMessage({ id: 'virtualVisit.test.openSettings' })}
            </ClickableElement>
          </div>
          <IconButton
            name="ico-24-close"
            size={16}
            fill={COLORS.white}
            onClick={() => showDescription(false)}
          />
        </div>
      )}
    </div>
  );
}

VisitHeader.propTypes = {
  full: PropTypes.bool,
  intl: PropTypes.object,
  noInfo: PropTypes.bool,
  collapsed: PropTypes.bool,
  noDescription: PropTypes.bool,
  roomDetails: PropTypes.object,
};

export default injectIntl(VisitHeader);



// WEBPACK FOOTER //
// ./src/components/VirtualVideoRoom/Components/VisitHeader/index.jsx